import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { AiOutlineFileExcel, AiOutlineFilePdf } from 'react-icons/ai';
import ReactHTMLTableToExcel from 'react-html-table-to-excel';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { FaRegCommentDots } from 'react-icons/fa';
import { DefaultTheme, useTheme } from 'styled-components';
import api from '../../services/api';
import { Table } from '../Table';
import { IReport } from '../../interfaces/IReport';
import { Pdf } from './Pdf';
import { ButtonsSocialNetwork } from '../../styles/components';

interface Props {
  route: string;
}

export const Report = ({ route }: Props) => {
  const { id } = useParams<{ id: string }>();
  const theme: DefaultTheme = useTheme();
  const [reports, setReports] = useState<IReport[]>([]);
  const [typeReport, setTypeReport] = useState<'instagram' | 'twitch'>(
    'twitch',
  );

  useEffect(() => {
    const handleGetReportInfluencer = async () => {
      try {
        const { data } = await api.get(`${route}/${id}/report`, {
          params: {
            reportContentFormat: typeReport,
          },
        });
        setReports(data);
      } catch (err: any) {
        if (err.response.data.message) {
          toast.error(err.response.data.message);
          return;
        }
        toast.error('Erro ao buscar relatório do influenciador');
      }
    };

    if (id) {
      handleGetReportInfluencer();
    }
  }, [id, typeReport, route]);

  const columns = useMemo(() => {
    if (typeReport === 'twitch') {
      return [
        {
          Header: 'Nome da live',
          accessor: 'reportContentNameLive',
        },
        {
          Header: route === 'influencers' ? 'Campanha' : 'Influenciador',
          accessor: 'name',
          Cell: ({ cell }: any) => {
            if (route === 'influencers') {
              const result = cell.row.original?.campaign ? (
                <Link
                  to={`/campaigns/${cell.row.original?.campaign?.id}/report`}
                >
                  {cell.row.original?.campaign?.nameCampaign}
                </Link>
              ) : (
                <span>-</span>
              );
              return result;
            }
            return (
              <Link to={`/influencers/${cell.row.original.id}/report`}>
                {cell.value}
              </Link>
            );
          },
        },
        {
          Header: 'Views',
          accessor: 'reportContentView',
        },
        {
          Header: 'Média/View',
          accessor: 'reportContentMed',
        },
        {
          Header: 'Follow',
          accessor: 'reportContentFollow',
        },
        {
          Header: 'Bits',
          accessor: 'reportContentBit',
        },
        {
          Header: 'Duração',
          accessor: 'reportContentTimeLive',
        },
      ];
    }
    return [
      {
        Header: 'Nome da live',
        accessor: 'reportContentNameLive',
      },
      {
        Header: route === 'influencers' ? 'Campanha' : 'Influenciador',
        accessor: 'name',
        Cell: ({ cell }: any) => {
          if (route === 'influencers') {
            const result = cell.row.original?.campaign ? (
              <Link to={`/campaigns/${cell.row.original?.campaign?.id}/report`}>
                {cell.row.original?.campaign?.nameCampaign}
              </Link>
            ) : (
              <span>-</span>
            );
            return result;
          }
          return (
            <Link to={`/influencers/${cell.row.original.id}/report`}>
              {cell.value}
            </Link>
          );
        },
      },

      {
        Header: 'Tipo',
        accessor: 'reportContentType',
        Cell: ({ cell }: any) => (
          <span className="d-block text-center">{cell.value}</span>
        ),
      },
      {
        Header: 'Descrição',
        accessor: 'reportContentText',
        Cell: ({ cell }: any) =>
          cell.value ? (
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id="button-tooltip-2">{cell.value}</Tooltip>}
            >
              {({ ref, ...triggerHandler }) => (
                <button
                  type="button"
                  {...triggerHandler}
                  className="d-flex justify-content-center w-100 bg-transparent border-0 align-items-center"
                >
                  <span ref={ref}>
                    <FaRegCommentDots
                      size={20}
                      color={theme.colors.textSecondary}
                    />
                  </span>
                </button>
              )}
            </OverlayTrigger>
          ) : (
            <span>-</span>
          ),
      },
      {
        Header: 'Likes',
        accessor: 'reportContentLike',
        Cell: ({ cell }: any) => (
          <span className="d-block text-center">{cell.value}</span>
        ),
      },
      {
        Header: 'Comentários',
        accessor: 'reportContentMsg',
        Cell: ({ cell }: any) => (
          <span className="d-block text-center">{cell.value}</span>
        ),
      },
    ];
  }, [typeReport, route]);

  return (
    <div>
      <Row className="d-flex align-items-center mt-4">
        <Col md={10} xs={8}>
          <ButtonsSocialNetwork className="d-flex align-items-center">
            <button
              type="button"
              onClick={() => setTypeReport('twitch')}
              className={typeReport === 'twitch' ? 'isActive' : ''}
            >
              Twitch
            </button>
            <button
              type="button"
              onClick={() => setTypeReport('instagram')}
              className={typeReport === 'instagram' ? 'isActive' : ''}
            >
              Instagram
            </button>
          </ButtonsSocialNetwork>
        </Col>

        <Col md={2} xs={4} className="d-flex justify-content-end">
          <div className="w-75 d-flex">
            <PDFDownloadLink
              document={<Pdf reports={reports} type={typeReport} />}
              fileName="relatorio-influencer.pdf"
              className="btn btn-danger w-50 d-flex align-items-center"
            >
              {({ loading }) =>
                loading ? 'Carregando...' : <AiOutlineFilePdf size={24} />
              }
            </PDFDownloadLink>

            <ReactHTMLTableToExcel
              id="test-table-xls-button"
              className="download-table-xls-button btn w-50 btn-success ml-2 d-flex align-items-center"
              table="table-to-report-influencer"
              filename="relatorio-comissao"
              sheet="tablexls"
              buttonText={<AiOutlineFileExcel size={24} />}
            />
          </div>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={12}>
          <Table
            columns={columns}
            pageCount={20}
            data={reports}
            id="table-to-report-influencer"
          />
        </Col>
      </Row>
    </div>
  );
};
